import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

const resolve = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-neutral-300"} />
      ))}
    </span>
  );
}

export function ProductReviews({ productId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!productId) return;
    api.get(`/products/${productId}/reviews`).then((res) => setData(res.data)).catch(() => {});
  }, [productId]);

  return (
    <section className="mt-20" data-testid="product-reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Customer Photos & Reviews</div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 tracking-tight">
            {data?.count ? `${data.count} Review${data.count === 1 ? "" : "s"}` : "No Reviews Yet"}
          </h2>
          {data?.count > 0 && (
            <div className="flex items-center gap-2 mt-2" data-testid="product-reviews-average">
              <Stars value={data.average} size={16} />
              <span className="text-sm text-neutral-500">{data.average.toFixed(1)} / 5</span>
            </div>
          )}
        </div>
        <Link to="/share-your-look" className="inline-flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wide text-charcoal border-b-2 border-charcoal hover:border-teal-600 hover:text-teal-600 transition-colors" data-testid="product-reviews-share-link">
          <Camera size={14} /> Share Yours For 15% Off
        </Link>
      </div>

      {data?.count ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.items.map((r) => (
            <div key={r.id} className="bg-[#F8F8F9] rounded-2xl p-6" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <Stars value={r.rating} />
                <span className="text-xs text-neutral-400">{formatDate(r.created_at)}</span>
              </div>
              {r.body && <p className="text-sm text-neutral-700 leading-relaxed mb-4">{r.body}</p>}
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mb-4 flex-wrap">
                  {r.photos.map((p) => (
                    <img key={p} src={resolve(p)} alt="Customer photo" className="w-24 h-24 object-cover rounded-xl" loading="lazy" />
                  ))}
                </div>
              )}
              <div className="text-xs font-heading font-bold uppercase tracking-wider text-neutral-500">
                {r.first_name} · Verified Buyer
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-500">
          Bought this pair? <Link to="/share-your-look" className="text-teal-700 underline">Send us a photo wearing them</Link> and we'll take 15% off your next pair.
        </p>
      )}
    </section>
  );
}
